import React, { useState } from "react";
import styled from "styled-components";

const FAQ_ITEMS = [
  {
    id: "chapter-7-what",
    question: "What is Chapter 7 bankruptcy?",
    answer: (
      <p>
        <a href="https://upsolve.org/learn/should-i-file-for-chapter-7-bankruptcy/">Chapter 7 bankruptcy</a> is a legal
        process that everyday people use to clear certain debts and get a financial fresh start.
      </p>
    ),
  },
  {
    id: "chapter-7-pros-cons",
    question: "What are the pros and cons of Chapter 7?",
    answer: (
      <p>
        Like everything, filing Chapter 7 bankruptcy has its{" "}
        <a href="https://upsolve.org/learn/pros-cons-filing-chapter-7-bankruptcy/">pros and cons</a>. The main pros are
        that collection calls and other actions (including{" "}
        <a href="https://upsolve.org/learn/stop-wage-garnishment/">wage garnishment</a>) must stop and, if your case is
        successful, you will get a financial fresh start by{" "}
        <a href="https://upsolve.org/learn/type-debt-erase-chapter-7-bankruptcy/">wiping out</a> (most of) your debt for
        good. The biggest cons are that you cannot get rid of some debts in Chapter 7 and your credit score can take a
        temporary hit.
      </p>
    ),
  },
  {
    id: "chapter-7-how",
    question: "How do you file Chapter 7 bankruptcy?",
    answer: (
      <p>
        To file Chapter 7 bankruptcy yourself, without hiring an expensive lawyer, follow our{" "}
        <a href="https://upsolve.org/learn/how-to-file-bankruptcy/">10-step guide</a> or{" "}
        <a href="https://my.upsolve.org/bankruptcy-screener">start our screener</a> to see if you qualify for help.
      </p>
    ),
  },
  {
    id: "chapter-7-eligible",
    question: "Who’s eligible to file Chapter 7?",
    answer: (
      <p>
        To be eligible for Chapter 7, you will need to pass the{" "}
        <a href="https://upsolve.org/learn/chapter-7-means-test-calculator/">means test</a>. This looks at your income
        and expenses to determine whether you have the means or ability to repay your debt. If you make too little to
        repay your debt, you pass the means test and are eligible to file Chapter 7.
      </p>
    ),
  },
  {
    id: "chapter-7-cost",
    question: "How much does it cost to file bankruptcy?",
    answer: (
      <>
        <p>
          There are four main types of <a href="https://upsolve.org/learn/how-much-does-bankruptcy-cost/">costs</a>{" "}
          associated with filing bankruptcy:
        </p>
        <ol>
          <li>
            <p>Court filing fees, which are $338 for Chapter 7 (you can apply for a waiver of these fees).</p>
          </li>
          <li>
            <p>
              Credit counseling course fees, which can range from $0-$50 (you can apply to{" "}
              <a href="https://upsolve.org/learn/using-a-fee-waiver-for-free-bankruptcy-credit-counseling/">waive</a>{" "}
              these fees).
            </p>
          </li>
          <li>
            <p>Miscellaneous costs such as printing, traveling to the court, etc.</p>
          </li>
          <li>
            <p>
              Attorney fees, which can range from $1,000-$1,500 or more, depending on the complexity of your case and
              where you live.
            </p>
          </li>
        </ol>
        <p>
          The good news is that you do not have to hire a lawyer to file successfully. Upsolve may be able to help you
          file Chapter 7 bankruptcy for free.
        </p>
      </>
    ),
  },
] as const;

const HomePageFAQ = (): React.ReactElement => {
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set<string>());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <section className="faq">
      <div className="faq__content content">
        <h1 className="faq__content__header">Is Upsolve Right for You?</h1>
        <StyledHomePageFAQ>
          {FAQ_ITEMS.map((item) => {
            const isOpen = openIds.has(item.id);
            const answerId = `faq-answer-${item.id}`;

            return (
              <div key={item.id} className={`faq__question ${isOpen ? "opened" : "closed"}`}>
                <button
                  type="button"
                  className={`faq__question__header ${isOpen ? "opened" : "closed"}`}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => toggle(item.id)}
                >
                  <span>{item.question}</span>
                  <img
                    src="/images/bankruptcy-assets/chevron-down.svg"
                    alt=""
                    width={33}
                    height={33}
                    loading="lazy"
                    decoding="async"
                    fetchPriority="low"
                  />
                </button>
                <div id={answerId} className="faq__answer" hidden={!isOpen}>
                  {item.answer}
                </div>
              </div>
            );
          })}
        </StyledHomePageFAQ>
      </div>
    </section>
  );
};

const StyledHomePageFAQ = styled.div`
  width: 100%;
  max-width: 633px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;

  @media screen and (max-width: 940px) {
    width: 100%;
    max-width: 340px;
    padding: 0 10px;
  }

  .faq__question {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 6.7px;
    border: 1px solid var(--Gray-3, #828282);
    width: 100%;
    padding: 12px 20px;
    font-size: 16px;

    @media screen and (max-width: 940px) {
      padding: 10px;
    }
  }

  .faq__question__header {
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
    line-height: inherit;
  }
  .faq__question__header span {
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    color: rgb(77, 85, 91);
    display: block;
    min-height: 48px;
    align-content: center;
  }

  .faq__question.opened .faq__question__header img {
    transform: rotate(180deg);
  }

  .faq__answer {
    margin-top: 0.75rem;
    p {
      margin: 0 0 0.75rem;
      &:last-child {
        margin-bottom: 0;
      }
    }
    ol,
    ul {
      margin: 0 0 0.75rem 1.25rem;
      padding: 0;
      font-size: inherit;
      line-height: inherit;
    }
    li {
      margin: 0 0 0.5rem;
      font-size: inherit;
      line-height: inherit;
    }
    li::marker {
      font-size: inherit;
    }
  }
`;

export default HomePageFAQ;
